import { formatCurrency, formatDate, formatNumber } from "@/utils/format";

import { Id } from "@cvx/_generated/dataModel";
import { Loader2 } from "lucide-react";
import StatusBadge from "@/components/Generic/StatusBadge";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useQuery as useConvexQuery } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

interface CreatorCampaign {
  contract: {
    _id: Id<"creatorContracts">;
    status: string;
    _creationTime: number;
  };
  campaign: {
    _id: Id<"campaigns">;
    name: string;
    type: "CiR" | "influencer" | "challenge";
    status: string;
    isPublic: boolean;
    applicationNotRequired?: boolean;
    endDate?: number;
    prizePool?: number;
    _creationTime: number;
  };
  app: {
    _id: Id<"apps">;
    name: string;
    logo: string;
  };
}

// Component for a single campaign row that fetches its own dashboard data
function CampaignRow({
  item,
  onCampaignClick,
}: {
  item: CreatorCampaign;
  onCampaignClick: (contractId: Id<"creatorContracts">) => void;
}) {
  const { data: dashboardData } = useQuery({
    ...convexQuery(api.dashboard.getCreatorDashboard, {
      contractId: item.contract._id,
    }),
    enabled: !!item.contract._id,
  });

  const videos = dashboardData?.videos || [];
  const approvedVideos = videos.filter(
    (v) => !v.status || v.status === "approved",
  );
  const totalViews = approvedVideos.reduce((sum, v) => sum + (v.views || 0), 0);
  const totalEarnings =
    (dashboardData?.earningsFlatFee || 0) +
    (dashboardData?.earningsPerVideoBonus || 0) +
    (dashboardData?.earningsCPM || 0);

  return (
    <tr
      className="cursor-pointer hover:bg-secondary/50"
      onClick={() => onCampaignClick(item.contract._id)}
    >
      <td className="px-6 py-4 text-sm text-primary font-medium">
        {item.campaign.name}
      </td>
      <td className="px-6 py-4 text-sm text-primary">
        <div className="flex items-center gap-2">
          {item.app.logo && (
            <img
              src={item.app.logo}
              alt={item.app.name}
              className="h-6 w-6 rounded-full"
            />
          )}
          <span>{item.app.name}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-primary">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            item.campaign.type === "CiR"
              ? "bg-blue-500/10 text-blue-500"
              : item.campaign.type === "influencer"
                ? "bg-purple-500/10 text-purple-500"
                : "bg-green-500/10 text-green-500"
          }`}
        >
          {item.campaign.type === "CiR"
            ? "Creator in Residence"
            : item.campaign.type === "influencer"
              ? "Influencer"
              : "Challenge"}
        </span>
      </td>
      <td className="px-6 py-4 text-sm">
        <StatusBadge status={item.campaign.status} />
      </td>
      <td className="px-6 py-4 text-right text-sm font-medium text-primary">
        {formatCurrency(totalEarnings)}
      </td>
      <td className="px-6 py-4 text-right text-sm text-primary">
        {formatNumber(totalViews)}
      </td>
      <td className="px-6 py-4 text-right text-sm text-primary">
        {approvedVideos.length}
      </td>
      <td className="px-6 py-4 text-sm text-primary">
        {item.campaign.endDate ? (
          <div className="flex flex-col">
            <span>
              {formatDate(new Date(item.campaign.endDate).toISOString())}
            </span>
            {(() => {
              const endDate = new Date(item.campaign.endDate);
              const now = new Date();
              const diffTime = endDate.getTime() - now.getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              if (diffDays > 0) {
                return (
                  <span className="text-xs text-primary/60">
                    {diffDays} day{diffDays !== 1 ? "s" : ""} left
                  </span>
                );
              } else if (diffDays === 0) {
                return (
                  <span className="text-xs text-primary/60">Ends today</span>
                );
              }
              return null;
            })()}
          </div>
        ) : (
          <span className="text-primary/60">No end date</span>
        )}
      </td>
    </tr>
  );
}

export default function CreatorCampaignsList() {
  const navigate = useNavigate();
  const { setContractId } = useAccount();

  // Get all creator campaigns
  const creatorCampaigns = useConvexQuery(
    api.dashboard.getCreatorCampaigns,
    {},
  );

  const handleCampaignClick = (contractId: Id<"creatorContracts">) => {
    setContractId(contractId);
    navigate({
      to: "/dashboard",
      search: {},
    });
  };

  if (creatorCampaigns === undefined) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  if (!creatorCampaigns || creatorCampaigns.length === 0) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center">
          <p className="mb-4 text-lg text-primary/60">No campaigns found</p>
          <p className="text-sm text-primary/60">
            You haven't joined any campaigns yet
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        {/* Campaigns Table */}
        <div className="rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Campaign Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    App
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                    Earnings
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                    Views
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                    Videos
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    End Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {creatorCampaigns.map((item) => (
                  <CampaignRow
                    key={item.contract._id}
                    item={item}
                    onCampaignClick={handleCampaignClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
